/**
 * Ações compactas da fila / pós-confirmação do Pronto Atendimento.
 * Reutiliza dialogs da agenda (WhatsApp, lembrete, questionários, cancelar).
 */
import { useMemo, useState } from "react";
import { IconButton, Stack, Tooltip } from "@mui/material";
import { Close, Notifications, WhatsApp } from "@mui/icons-material";
import { AppointmentReminderModal } from "@/components/common/AppointmentReminderModal";
import { resolveAppointmentReminderWhatsAppContext } from "@/components/common/appointmentReminderWhatsApp";
import ScheduleCancelDialog from "@/components/schedule/ScheduleCancelDialog";
import { SendQuestionnairesTrigger } from "@/components/schedule/components/SendQuestionnairesTrigger";
import { useClinicalRegisterContext } from "@/contexts/ClinicalRegisterContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { oids } from "@/configs/Settings";
import { SendWhatsAppMessageDialog } from "src-next/modules/crm-clinical/messaging";
import {
  useMessagingTemplatesForSend,
  type MessagingAppointment,
  type MessagingPatient,
} from "src-next/modules/crm-clinical/messaging";
import { resolveOptionalAppointmentBasedOn } from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import type { ProntoActionAppointment } from "./mapProntoAppointmentToQueueItem";

interface ProntoAtendimentoQueueActionsProps {
  appointment: ProntoActionAppointment;
  /** Dispara refetch da fila após cancelar. */
  onRefetch?: () => void;
  /** Se true, após cancelar com sucesso chama onCancelled (ex.: fechar dialog Attend Now). */
  onCancelled?: () => void;
}

export function ProntoAtendimentoQueueActions({
  appointment,
  onRefetch,
  onCancelled,
}: ProntoAtendimentoQueueActionsProps) {
  const { organizationId } = useOrganizationContext();
  const { user } = useSessionContext();
  const { inProgress } = useClinicalRegisterContext();
  const { templates: messagingTemplates } = useMessagingTemplatesForSend({
    organizationId,
  });

  const [whatsAppOpen, setWhatsAppOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const status = appointment.status || appointment.originalData?.status || "";
  const isBooked = status === "booked";
  const isCancelled = status === "cancelled";
  const phone = appointment.patientPhone;

  const patientRef =
    appointment.originalData?.participant?.find((p) =>
      p.actor?.reference?.includes("Patient/"),
    )?.actor?.reference || null;

  const patientId = patientRef?.split("/")?.[1];
  const cpfFromId =
    patientId && patientId.includes("-")
      ? patientId.split("-").slice(1).join("-")
      : undefined;

  const reminderContext = useMemo(() => {
    const original = appointment.originalData;
    // Teleconsulta se há sala Whereby no Appointment (remoto/chat no PA)
    const vs = original?.virtualService as
      | Array<{ addressUrl?: string }>
      | { addressUrl?: string }
      | undefined;
    const hasVirtualService = Boolean(
      (Array.isArray(vs) && vs[0]?.addressUrl) ||
        (!Array.isArray(vs) && vs?.addressUrl),
    );

    return resolveAppointmentReminderWhatsAppContext({
      appointment: {
        ...appointment,
        originalData: original,
      },
      patient: undefined,
      locationResource: undefined,
      locationTeleconsulting: hasVirtualService,
      patientPhone: phone,
    });
  }, [appointment, phone]);


  const appointmentReferenceForTaskSend = useMemo(
    () =>
      resolveOptionalAppointmentBasedOn(
        appointment.originalData?.id ?? appointment.id,
      ),
    [appointment.id, appointment.originalData?.id],
  );

  const hasActiveEncounter = Boolean(
    appointment.id &&
      inProgress &&
      String(inProgress) === String(appointment.id),
  );

  const messagingAppointment: MessagingAppointment = useMemo(
    () => ({
      id: appointment.id ?? appointment.originalData?.id ?? "appointment",
      startAtIso:
        appointment.startTime ?? appointment.originalData?.start ?? undefined,
    }),
    [appointment],
  );

  const messagingPatient: MessagingPatient | null = useMemo(() => {
    if (!patientId) return null;
    return {
      id: patientId,
      fullName: appointment.patientName || "Paciente",
      cpf: cpfFromId,
      phone: phone ? String(phone) : undefined,
    };
  }, [appointment.patientName, cpfFromId, patientId, phone]);

  const setRefetchCompat = (value: boolean | ((prev: boolean) => boolean)) => {
    const next = typeof value === "function" ? value(false) : value;
    if (next) {
      onRefetch?.();
      onCancelled?.();
    }
  };

  if (isCancelled) return null;

  return (
    <>
      <Stack direction="row" spacing={0.25} alignItems="center">
        {phone && (
          <Tooltip title="Enviar WhatsApp">
            <IconButton
             
              sx={{ color: "success.main" }}
              onClick={() => setWhatsAppOpen(true)}
              aria-label="Enviar WhatsApp"
            >
              <WhatsApp fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {phone && isBooked && (
          <Tooltip title="Enviar lembrete de consulta">
            <IconButton
             
              color="secondary"
              onClick={() => setReminderOpen(true)}
              aria-label="Enviar lembrete"
            >
              <Notifications fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <SendQuestionnairesTrigger
          patientReference={patientRef}
          variant="compact"
          appointmentStatus={status}
          appointmentReference={appointmentReferenceForTaskSend}
          hasActiveEncounter={hasActiveEncounter}
          patientName={reminderContext.patientName}
          patientPhone={phone}
        />

        {isBooked && (
          <Tooltip title="Cancelar consulta">
            <IconButton
             
              color="error"
              onClick={() => setCancelOpen(true)}
              aria-label="Cancelar consulta"
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {reminderOpen && (
        <AppointmentReminderModal
          isOpen={reminderOpen}
          onClose={() => setReminderOpen(false)}
          reminderContext={reminderContext}
        />
      )}

      {messagingPatient && (
        <SendWhatsAppMessageDialog
          open={whatsAppOpen}
          onClose={() => setWhatsAppOpen(false)}
          appointment={messagingAppointment}
          patient={messagingPatient}
          templates={messagingTemplates}
          organizationId={organizationId}
          practitionerId={`${oids.cpf}-${user?.username}`}
          taskPatientReference={patientRef}
        />
      )}

      {cancelOpen && (
        <ScheduleCancelDialog
          isOpen={cancelOpen}
          setIsOpen={setCancelOpen}
          appointment={appointment.originalData}
          setRefetch={setRefetchCompat}
          payment={[]}
          isPaid={false}
        />
      )}
    </>
  );
}
